import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { LuPaperclip, LuSend, LuX, LuCheck, LuUserCheck } from "react-icons/lu";
import { useSupportChat, useSupportConversations } from "../../hooks/useSupportChat";
import {
  assignSupportConversation,
  setSupportStatus,
} from "../../Services/supportService";

const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "pending", label: "In Progress" },
  { key: "mine", label: "Assigned to me" },
  { key: "closed", label: "Closed" },
];

const statusColor = {
  open: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  closed: "bg-gray-100 text-gray-500",
};

const MessageBubble = ({ message, isMine }) => {
  const isImage = message.attachment_type?.startsWith("image/");
  return (
    <div className={`flex mb-3 ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
          isMine
            ? "bg-[#425F80] text-white rounded-br-sm"
            : message.sender_type === "system"
            ? "bg-yellow-50 text-gray-600 text-sm italic"
            : "bg-gray-100 text-gray-800 rounded-bl-sm"
        }`}
      >
        {message.attachment_url &&
          (isImage ? (
            <a href={message.attachment_url} target="_blank" rel="noreferrer">
              <img
                src={message.attachment_url}
                alt={message.attachment_name || "attachment"}
                className="rounded-lg mb-1 max-h-52 object-cover"
              />
            </a>
          ) : (
            <a
              href={message.attachment_url}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-2 underline text-sm mb-1 ${
                isMine ? "text-white" : "text-blue-600"
              }`}
            >
              <LuPaperclip size={14} />
              {message.attachment_name || "Attachment"}
            </a>
          ))}
        {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
        <p className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-gray-400"}`}>
          {moment(message.created_at).format("MMM D, h:mm A")}
        </p>
      </div>
    </div>
  );
};

const SupportConsole = () => {
  const [tab, setTab] = useState("open");
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const filters = useMemo(
    () => (tab === "mine" ? { mine: "true" } : { status: tab }),
    [tab]
  );
  const { conversations, loading: listLoading, refresh } =
    useSupportConversations(filters);
  const { messages, loading, sending, send } = useSupportChat(activeId);

  const activeConv = conversations.find((c) => c.id === activeId);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !file) || sending) return;
    const body = text;
    const attachment = file;
    setText("");
    setFile(null);
    try {
      await send({ body, file: attachment });
    } catch {
      setText(body);
      setFile(attachment);
    }
  };

  const handleAssign = async () => {
    if (!activeId) return;
    await assignSupportConversation(activeId);
    refresh();
  };

  const handleClose = async () => {
    if (!activeId) return;
    await setSupportStatus(activeId, "closed");
    refresh();
  };

  return (
    <div className="flex h-[calc(100vh-120px)] bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Conversation list */}
      <div className="w-80 border-r border-gray-100 flex flex-col">
        <div className="flex border-b border-gray-100 text-xs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setActiveId(null);
              }}
              className={`flex-1 py-3 font-medium ${
                tab === t.key
                  ? "text-[#425F80] border-b-2 border-[#425F80]"
                  : "text-gray-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {listLoading ? (
            <p className="text-center text-gray-400 py-6 text-sm">Loading…</p>
          ) : conversations.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">No conversations</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${
                  activeId === c.id ? "bg-gray-50" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-gray-800 truncate">
                    {c.subject || `Advisor ${String(c.advisor_id).slice(0, 8)}`}
                  </span>
                  {c.unread_agent > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">
                      {c.unread_agent}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full ${
                      statusColor[c.status] || ""
                    }`}
                  >
                    {c.status}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {moment(c.last_message_at).fromNow()}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat pane */}
      <div className="flex-1 flex flex-col">
        {!activeId ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            Select a conversation
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-800">
                  {activeConv?.subject ||
                    `Advisor ${String(activeConv?.advisor_id || "").slice(0, 8)}`}
                </h3>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full ${
                    statusColor[activeConv?.status] || ""
                  }`}
                >
                  {activeConv?.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAssign}
                  className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50"
                >
                  <LuUserCheck size={14} /> Assign to me
                </button>
                {activeConv?.status !== "closed" && (
                  <button
                    onClick={handleClose}
                    className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 text-red-500"
                  >
                    <LuCheck size={14} /> Close
                  </button>
                )}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
              {loading ? (
                <p className="text-center text-gray-400 mt-8">Loading…</p>
              ) : (
                messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    isMine={m.sender_type === "agent"}
                  />
                ))
              )}
            </div>

            <form onSubmit={handleSend} className="border-t border-gray-100 px-4 py-3">
              {file && (
                <div className="flex items-center gap-2 mb-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-1.5 w-fit">
                  <LuPaperclip size={14} />
                  <span className="truncate max-w-[200px]">{file.name}</span>
                  <button type="button" onClick={() => setFile(null)}>
                    <LuX size={14} />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-500 hover:text-[#425F80]"
                >
                  <LuPaperclip size={20} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a reply…"
                  className="flex-1 border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#425F80]/30"
                />
                <button
                  type="submit"
                  disabled={sending || (!text.trim() && !file)}
                  className="p-2.5 rounded-full bg-[#425F80] text-white disabled:opacity-40"
                >
                  <LuSend size={18} />
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportConsole;
